import type { File, FileFolder, UserOfferMembership } from '@prisma/client'
import type { FileFolderAction } from './actions'
import { can, internalRoles } from '~/authorization'
import type { FileFolderType, UserRole } from '~/prisma/enums'
import type { UserOfferMembershipRole } from '~/types'

interface User {
  roles: UserRole[]
  email: string
}

interface OfferReport {
  userMemberships: Pick<UserOfferMembership, 'userEmail' | 'role'>[]
}
interface OfferPreparation {
  userMemberships: Pick<UserOfferMembership, 'userEmail' | 'role'>[]
  externalStakeholders: { user: { email: string }, rolesOfCreatorAtCreation: UserRole[] }[]
}

export interface PermissionConfigParams {
  rootFolder?: { offerReport: OfferReport | null, offerPreparation: OfferPreparation | null } | null
  currFolder?: Pick<FileFolder, 'isCreatedBySystem' | 'createdByEmail'> | null
  currFile?: Pick<File, 'createdByEmail'> | null
}
type PermissionConfig = Record<FileFolderAction, Readonly<UserRole[]> | ((user: User, param: PermissionConfigParams) => boolean)>

export const FILEFOLDER_ADMIN_ROLES: UserRole[] = ['admin', 'departmentLead']
const customerRoles: UserRole[] = ['admin', 'projectManager', 'departmentLead', 'consignee', 'receptionist', 'rentalEmployee', 'salesEmployee', 'disponent', 'workshopEmployee', 'marketingEmployee', 'bookkeeping', 'storageLead', 'storageEmployee', 'serviceProjectEmployee', 'siteManager', 'serviceProjectFitter', 'specialRoleBenni', 'workshopOfficeEmployee']
const OFFER_REPORT_MEMBERSHIP_ROLES: UserOfferMembershipRole[] = ['on-site-manager', 'commercial-project-manager', 'director']
const OFFER_REPORTING_PAGE_URL = '/my-hos/service-project/reporting'

/** Permission Config */
const permissionConfigs: Readonly<Record<FileFolderRootType, PermissionConfig>> = {
  Offer: {
    View: internalRoles,
    MoveFile: internalRoles,
    UpdateFiles: internalRoles,
    Create: FILEFOLDER_ADMIN_ROLES,
    UpdateFolder: FILEFOLDER_ADMIN_ROLES,
    Delete: FILEFOLDER_ADMIN_ROLES,
    ShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    WithdrawShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    MoveFolder: moveFolder,
  },

  Customer: {
    View: customerRoles,
    MoveFile(user, { currFile }) {
      if (
        !currFile // No current file
        || !isAuthedUser(user.roles, customerRoles) // User isn't a customer role
        || (!isAuthedUser(user.roles, FILEFOLDER_ADMIN_ROLES) && currFile.createdByEmail !== user.email) // User isn't an admin and the file isn't created by the user
      ) {
        return false
      }
      return true
    },
    UpdateFiles: customerRoles,
    Create: FILEFOLDER_ADMIN_ROLES,
    UpdateFolder: FILEFOLDER_ADMIN_ROLES,
    Delete: FILEFOLDER_ADMIN_ROLES,
    ShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    WithdrawShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    MoveFolder: moveFolder,
  },

  OfferReport: {
    View(user) {
      return can(user.roles, OFFER_REPORTING_PAGE_URL)
    },
    MoveFile: canUpdateOfferReport,
    UpdateFiles(user) {
      return can(user.roles, OFFER_REPORTING_PAGE_URL)
    },
    Create: canUpdateOfferReport,
    UpdateFolder: canUpdateOfferReport,
    MoveFolder: canUpdateOfferReport,
    Delete: canUpdateOfferReport,
    ShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    WithdrawShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
  },

  OfferPreparation: {
    View: canAccessOfferPreparation,
    MoveFile: canUpdateOfferPreparation,
    UpdateFiles: canAccessOfferPreparation,
    Create: canUpdateOfferPreparation,
    UpdateFolder: canUpdateOfferPreparation,
    MoveFolder: canUpdateOfferPreparation,
    Delete: canUpdateOfferPreparation,
    ShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
    WithdrawShareWithCustomer: FILEFOLDER_ADMIN_ROLES,
  },
}

export function canAccessFileFolder(action: FileFolderAction, user: User, type: FileFolderType, configParams?: PermissionConfigParams): boolean {
  const rootType = fileFolderRootType[type]
  const allowedRoles = permissionConfigs[rootType][action]
  if (typeof allowedRoles === 'function') {
    return allowedRoles(user, configParams ?? {})
  }
  return isAuthedUser(user.roles, allowedRoles)
}

/** Utility functions */
function moveFolder(user: User, { currFolder }: PermissionConfigParams) {
  if (!currFolder) {
    return false
  }
  if (!currFolder.isCreatedBySystem && isAuthedUser(user.roles)) {
    return true
  }
  return false
}

function isAuthedUser(userRoles: UserRole[], allowedRoles: Readonly<UserRole[]> = FILEFOLDER_ADMIN_ROLES): boolean {
  return hasAnyRole(userRoles, allowedRoles)
}

/** Offer Report Utils */
function canUpdateOfferReport(user: User, { rootFolder }: PermissionConfigParams): boolean {
  // Admin user always has permissions
  if (isAuthedUser(user.roles, ['admin'])) {
    return true
  }

  // Get the current user's membership in the offer report
  const userMembership = rootFolder?.offerReport?.userMemberships.find(m => m.userEmail === user.email)
  // Check if the user has a correct offer membership role
  return !!userMembership && OFFER_REPORT_MEMBERSHIP_ROLES.includes(userMembership.role as UserOfferMembershipRole)
}

/** Offer Preparation Utils */
function canUpdateOfferPreparation(user: User, { rootFolder }: PermissionConfigParams): boolean {
  // Admin user always has permissions
  if (isAuthedUser(user.roles, ['admin'])) {
    return true
  }

  // Get the current user's membership in the offer report
  const userMembership = rootFolder?.offerPreparation?.userMemberships.find(m => m.userEmail === user.email)
  // Check if the user has a correct offer membership role
  return !!userMembership && OFFER_REPORT_MEMBERSHIP_ROLES.includes(userMembership.role as UserOfferMembershipRole)
}

function canAccessOfferPreparation(user: User, { rootFolder }: PermissionConfigParams): boolean {
  if (isAuthedUser(user.roles, ['externalServiceProjectStakeholder'])) {
    return !!rootFolder?.offerPreparation?.externalStakeholders.some(
      stakeholder => stakeholder.user.email === user.email && hasAnyRole(stakeholder.rolesOfCreatorAtCreation, internalRoles),
    )
  }

  return canUpdateOfferPreparation(user, { rootFolder })
}
